<script setup lang="ts">
  import { ref, onMounted, onUnmounted } from "vue";
  import { useI18n } from "vue-i18n";
  import { useNotification, Button, Text, IconRfid, IconSuccess } from "@ui-kit";
  import { usePowerRfid, useReadRfid } from "@plugins";
  import { ScanScreen, ScanScreenStart, ProductShortCard } from "@src/components";
  import { useBarcode, useWriteRfid, PASSWORD_FOR_WRITE_EPC, LOCK_MEM_TYPE } from "@plugins";
  import { useProfileOptions } from "@src/hooks";
  import { WTFIRInputType, WTFIRTypeData, useGetWTFIR, useLazyProductsLoad, usePairEPC } from "@src/api";

  const emit = defineEmits<{ (e: "close"): void }>();

  const barcode = useBarcode();
  const powerRfid = usePowerRfid();
  const readRfid = useReadRfid();
  const writeRfid = useWriteRfid();
  const { t } = useI18n();
  const { show, clear } = useNotification();
  const { execute: getWTFIR } = useGetWTFIR();
  const { execute: getProduct } = useLazyProductsLoad();
  const { profile } = useProfileOptions();
  const { execute: pairEPC } = usePairEPC();

  const productId = ref<string | null>(null);
  const barcodeForWriting = ref<string | null>(null);
  const epcForWriting = ref<string | null>(null);
  const writeStatus = ref<string | null>(null);

  const handleScanBarcode = async (data: string) => {
    clear();

    try {
      const resultWTFIR = await getWTFIR({ data, input_type: WTFIRInputType.barcode });

      if (resultWTFIR?.isError.value || resultWTFIR.data.value.type_data !== WTFIRTypeData.barcode) {
        throw { title: t("Ошибка, попробуйте ещё раз"), type: "error" };
      }

      if (!resultWTFIR.data.value.items?.at(0)) {
        throw { title: t("Товар не найден"), type: "error" };
      }

      const resultProduct = await getProduct({ item_id: [resultWTFIR.data.value.items?.at(0)] });

      if (resultProduct?.isError.value) {
        throw { title: t("Ошибка, попробуйте ещё раз"), type: "error" };
      }

      if (resultProduct.data.value.items?.at(0)?.is_marked) {
        throw { title: t("Отсканируйте не маркированный товар"), type: "error" };
      }

      const resultPairEPC = await pairEPC({ barcode: data });

      if (resultPairEPC?.isError.value) {
        throw { title: t("Ошибка, попробуйте ещё раз"), type: "error" };
      }

      productId.value = resultWTFIR.data.value.items?.at(0);
      epcForWriting.value = resultPairEPC.data.value.epc;
      barcodeForWriting.value = data;
      barcode.unsubscribe();

      if (profile.value.constants.rfid.power_once_read) {
        await powerRfid.set(profile.value.constants.rfid.power_once_read);
      }

      readRfid.subscribe((data) => handleScanRfid(data.value));
    } catch (e) {
      show({ title: e.title, type: e.type });
    }
  };

  const handleScanRfid = async (data: string) => {
    try {
      if (!data) {
        throw { title: t("EPC метка не найдена"), type: "error" };
      }

      const resultWTFIR = await getWTFIR({ data, input_type: WTFIRInputType.rfid });

      if (resultWTFIR.isError.value) {
        throw { title: t("Ошибка, попробуйте ещё раз"), type: "error" };
      }

      if (resultWTFIR.data.value.type_data !== WTFIRTypeData.epcClear) {
        if (resultWTFIR.data.value.gtin !== barcodeForWriting.value) {
          throw { title: t("Приложите чистую EPC метку"), type: "warning" };
        }
        show({ title: t("EPC метка уже записана"), type: "success" });
        writeStatus.value = "already-written";
      } else {
        if (profile.value.constants.rfid.power_write) {
          await powerRfid.set(profile.value.constants.rfid.power_write);
        }

        const resultWriteRfidData = await writeRfid.writeData({ epc: epcForWriting.value, filterData: data });

        if (!resultWriteRfidData) {
          throw { title: t("Ошибка записи EPC"), type: "error" };
        }

        const resultWriteRfidPassword = await writeRfid.writePassword({
          newPassword: PASSWORD_FOR_WRITE_EPC,
          filterData: epcForWriting.value,
        });

        if (!resultWriteRfidPassword) {
          throw { title: t("Ошибка записи буфера"), type: "error" };
        }
        const resultLockMem = await writeRfid.lockMem({
          password: PASSWORD_FOR_WRITE_EPC,
          filterData: epcForWriting.value,
          type: LOCK_MEM_TYPE.ALL,
        });

        if (!resultLockMem) {
          throw { title: t("Ошибка блокировки памяти"), type: "error" };
        }
        show({ title: t("EPC метка успешно записана"), type: "success" });
        writeStatus.value = "success";
      }

      setTimeout(handleGoToHome, 2000);

      readRfid.unsubscribe();
    } catch (e) {
      show({ title: e.title, type: e.type });
    }
  };

  const handleGoToHome = () => {
    productId.value = null;
    epcForWriting.value = null;

    emit("close");
  };

  onMounted(() => {
    barcode.subscribe(handleScanBarcode);
  });

  onUnmounted(() => {
    powerRfid.set(powerRfid.prevPower.value);
    barcode.unsubscribe();
    readRfid.unsubscribe();
  });
</script>

<template>
  <div class="widget__label-write-epc" data-testid="widget-write-epc-nonmarked">
    <ScanScreen
      v-if="!productId && !epcForWriting"
      data-testid="label-write-epc"
      @close="emit('close')"
      :modal="{ title: $t('Запись не маркированного ШК'), subtitle: $t('Отсканируйте ШК не маркируемого товара') }"
    >
      <ScanScreenStart />
    </ScanScreen>
    <div v-else-if="writeStatus === 'success'" class="label-write-epc__write-epc" data-testid="write-epc-success">
      <ProductShortCard :productId="productId" @click:back="emit('close')" />
      <div class="write-epc__footer">
        <span class="correct-pair__icon">
          <IconSuccess style="width: 30px; height: 30px; stroke: var(--sf-base-icon-success)" />
        </span>
        <div class="write-epc__footer-description">
          <Text font="body-bold-18">{{ $t("EPC метка успешно записана") }}</Text>
          <Text font="body-regular-14" color="secondary">{{ $t("Товар готов к кодированию аларма") }}</Text>
        </div>
        <Button variant="secondary" @click="emit('close')" data-testid="write-epc-btn-main">{{
          $t("На главную")
        }}</Button>
      </div>
    </div>
    <div
      v-else-if="writeStatus === 'already-written'"
      class="label-write-epc__write-epc"
      data-testid="write-epc-already-written"
    >
      <ProductShortCard :productId="productId" @click:back="emit('close')" />
      <div class="write-epc__footer">
        <span class="correct-pair__icon">
          <IconSuccess style="width: 30px; height: 30px; stroke: var(--sf-base-icon-success)" />
        </span>
        <div class="write-epc__footer-description">
          <Text font="body-bold-18">{{ $t("EPC метка уже записана") }}</Text>
          <Text font="body-regular-14" color="secondary">{{ $t("Товар готов к кодированию аларма") }}</Text>
        </div>
        <Button variant="secondary" @click="emit('close')" data-testid="write-epc-btn-main">{{
          $t("На главную")
        }}</Button>
      </div>
    </div>

    <div v-else class="label-write-epc__write-epc" data-testid="read-epc-clean">
      <ProductShortCard :productId="productId" @click:back="emit('close')" />
      <div class="write-epc__footer">
        <IconRfid />
        <div class="write-epc__footer-description">
          <Text font="body-bold-18">{{ $t("Поднесите чистый Hand Tag") }}</Text>
          <Text font="body-regular-14" color="secondary">{{ $t("Для записи не маркированного товара") }}</Text>
        </div>
        <Button variant="secondary" @click="emit('close')" data-testid="write-epc-btn-cancel">{{
          $t("Отмена")
        }}</Button>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .widget__label-write-epc {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    z-index: 1;
  }

  .label-write-epc__write-epc {
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: var(--sf-base-background-secondary);
  }

  .write-epc__footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: var(--sf-base-background-white);
    box-shadow: var(--sf-shadow-top-s);
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    padding: 20px 16px;
    margin-top: auto;
  }

  .write-epc__footer-description {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 16px;
    text-align: center;
  }

  .write-epc__footer :deep(.button) {
    width: 100%;
    margin-top: 38px;
  }
  .correct-pair__icon {
    display: flex;
    border-width: 2px;
    border-style: solid;
    border-radius: 50%;
    padding: 6px;
    margin: 12px;
    border-color: var(--sf-base-icon-success);
  }
</style>
